/**
 * PostHog active backfill pipeline wizard for /admin/conversion-roi.
 *
 * Two-step flow:
 *   1. Enter PostHog host + project_id + Personal API key → Test → Save (encrypted)
 *   2. Click "Backfill 7d" → events upserted into conversion_events → ROI report refreshes
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Plug, CheckCircle2, AlertTriangle, RefreshCw, Trash2, ShieldCheck, ExternalLink, History } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function ProbeBadge({ state }) {
  if (state === 'idle') return null;
  if (state === 'testing') return <Badge className="bg-slate-700/60 text-slate-200">Testing…</Badge>;
  if (state === 'ok') return <Badge className="bg-emerald-500/20 text-emerald-200 border border-emerald-400/40"><CheckCircle2 className="w-3 h-3 inline mr-1" /> Connected</Badge>;
  return <Badge className="bg-rose-500/20 text-rose-200 border border-rose-400/40"><AlertTriangle className="w-3 h-3 inline mr-1" /> Failed</Badge>;
}

export default function PostHogPipelineWizard({ onBackfillComplete }) {
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState({
    posthog_host: 'https://us.i.posthog.com',
    project_id: '',
    api_key: '',
  });
  const [probe, setProbe] = useState({ state: 'idle', msg: null });
  const [backfillBusy, setBackfillBusy] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API}/api/posthog-pipeline/config`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      setConfig(d.connected ? d : null);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const callApi = async (path, method, body) => {
    const token = localStorage.getItem('token');
    const r = await fetch(`${API}${path}`, {
      method,
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    });
    const text = await r.text();
    let json = null;
    try { json = JSON.parse(text); } catch { /* non-json */ }
    return { ok: r.ok, status: r.status, body: json || text };
  };

  const test = async () => {
    setProbe({ state: 'testing', msg: null });
    const payload = { ...form, project_id: Number(form.project_id) };
    const r = await callApi('/api/posthog-pipeline/test', 'POST', payload);
    if (r.ok && r.body.ok) {
      setProbe({ state: 'ok', msg: r.body.project_name ? `Reachable · project "${r.body.project_name}"` : 'Reachable' });
    } else {
      setProbe({ state: 'fail', msg: r.body?.error || r.body?.detail || `HTTP ${r.status}` });
    }
  };

  const connect = async () => {
    setProbe({ state: 'testing', msg: null });
    const payload = { ...form, project_id: Number(form.project_id) };
    const r = await callApi('/api/posthog-pipeline/connect', 'POST', payload);
    if (r.ok) {
      setProbe({ state: 'ok', msg: 'Connected' });
      setForm({ posthog_host: 'https://us.i.posthog.com', project_id: '', api_key: '' });
      refresh();
    } else setProbe({ state: 'fail', msg: r.body?.detail || `HTTP ${r.status}` });
  };

  const backfill = async (days) => {
    setBackfillBusy(true);
    setBackfillResult(null);
    const r = await callApi(`/api/posthog-pipeline/backfill?days=${days}`, 'POST');
    setBackfillBusy(false);
    if (r.ok) {
      setBackfillResult(r.body);
      onBackfillComplete?.(r.body);
      refresh();
    } else setBackfillResult({ error: r.body?.detail || `HTTP ${r.status}` });
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect PostHog? Encrypted API key will be deleted. Already-imported conversion_events are kept.')) return;
    await callApi('/api/posthog-pipeline/config', 'DELETE');
    refresh();
    setBackfillResult(null);
  };

  return (
    <Card className="bg-slate-950 border-emerald-400/30" data-testid="posthog-pipeline-wizard">
      <CardHeader>
        <CardTitle className="text-base text-white flex items-center gap-2">
          <Plug className="w-4 h-4 text-emerald-300" />
          PostHog active pipeline — auto-import conversion events
          <Badge className="bg-emerald-500/20 text-emerald-200 border border-emerald-400/40 text-[9px] font-mono tracking-widest ml-1">P1</Badge>
        </CardTitle>
        <p className="text-sm text-slate-200 mt-1">Skip the webhook setup. Paste a PostHog Personal API key + project ID → we pull your signup/purchase/churn events directly and join them with AI cost. Token encrypted with AES-128.</p>
      </CardHeader>
      <CardContent className="space-y-4">
        {config ? (
          <div className="rounded-md bg-emerald-500/5 border border-emerald-400/30 p-3">
            <div className="flex items-center justify-between gap-3">
              <div className="text-sm">
                <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                  <ShieldCheck className="w-4 h-4 text-emerald-300" />
                  <span className="text-white font-bold">Connected</span>
                  <code className="text-xs text-emerald-300 font-mono">{config.project_name || `project ${config.project_id}`}</code>
                  <code className="text-[10px] text-slate-400 font-mono">{config.posthog_host}</code>
                </div>
                {config.last_backfill_at && (
                  <div className="text-xs text-slate-300">
                    Last backfill: {new Date(config.last_backfill_at).toLocaleString()} · {config.last_backfill_count} events
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={() => backfill(7)} disabled={backfillBusy} className="bg-emerald-600 hover:bg-emerald-500 text-white h-8 text-xs" data-testid="posthog-backfill-7d-btn">
                  {backfillBusy ? 'Syncing…' : <><RefreshCw className="w-3 h-3 mr-1" /> Backfill 7d</>}
                </Button>
                <Button size="sm" onClick={() => backfill(30)} disabled={backfillBusy} className="bg-emerald-700 hover:bg-emerald-600 text-white h-8 text-xs" data-testid="posthog-backfill-30d-btn">
                  <History className="w-3 h-3 mr-1" /> 30d
                </Button>
                <Button size="sm" variant="ghost" onClick={disconnect} className="text-rose-300 hover:text-rose-200 h-8 px-2">
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
            {backfillResult && (
              <div className="mt-3 text-xs">
                {backfillResult.error ? (
                  <div className="text-rose-300">{backfillResult.error}</div>
                ) : (
                  <div className="text-emerald-300">
                    ✓ {backfillResult.events_fetched} fetched · {backfillResult.events_upserted} imported · {backfillResult.events_skipped} skipped · window {backfillResult.window_days}d
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="rounded-md bg-slate-950 border border-slate-700 p-3" data-testid="posthog-form">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-bold text-white">PostHog credentials</div>
              <ProbeBadge state={probe.state} />
            </div>
            <select
              value={form.posthog_host}
              onChange={(e) => setForm({ ...form, posthog_host: e.target.value })}
              className="w-full mb-2 bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs text-white"
              data-testid="posthog-host-select"
            >
              <option value="https://us.i.posthog.com">US Cloud (us.i.posthog.com)</option>
              <option value="https://eu.i.posthog.com">EU Cloud (eu.i.posthog.com)</option>
              <option value="https://app.posthog.com">Legacy (app.posthog.com)</option>
            </select>
            <input
              type="number"
              placeholder="Project ID (e.g., 12345)"
              value={form.project_id}
              onChange={(e) => setForm({ ...form, project_id: e.target.value })}
              className="w-full mb-2 bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs text-white"
              data-testid="posthog-project-id-input"
            />
            <input
              type="password"
              placeholder="Personal API Key (phx_…)"
              value={form.api_key}
              onChange={(e) => setForm({ ...form, api_key: e.target.value })}
              className="w-full mb-2 bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs text-white"
              data-testid="posthog-api-key-input"
            />
            <div className="flex gap-2 mt-1 flex-wrap">
              <Button size="sm" variant="outline" onClick={test} disabled={!form.project_id || !form.api_key} className="text-xs h-7" data-testid="posthog-test-btn">Test</Button>
              <Button size="sm" onClick={connect} disabled={!form.project_id || !form.api_key} className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs h-7" data-testid="posthog-connect-btn">Save & connect</Button>
              <a href="https://posthog.com/docs/api#how-to-obtain-a-personal-api-key" target="_blank" rel="noreferrer" className="ml-auto text-xs text-slate-300 hover:text-emerald-300 flex items-center gap-1">
                Get a Personal API Key <ExternalLink className="w-3 h-3" />
              </a>
            </div>
            {probe.msg && <div className={`mt-2 text-xs ${probe.state === 'ok' ? 'text-emerald-300' : 'text-rose-300'}`}>{probe.msg}</div>}
            <div className="text-[11px] text-slate-300 italic mt-2">
              We track: <code className="text-emerald-300">signup</code>, <code className="text-emerald-300">subscription_started</code>, <code className="text-emerald-300">purchase_completed</code>, <code className="text-emerald-300">upgrade</code>, <code className="text-emerald-300">churn</code> (and common synonyms). Edit `event_map` via the API for custom event names.
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
